import { useEffect, useState, type ReactNode } from 'react';
import DetailSection from './DetailSection';
import { getReviewByLessonId } from '../services/reviews';
import type { Review } from '../types/chat';

/**
 * The review a player left on a delivered lesson, at the top of both sides of
 * the exchange, and on the coach's side the player's identity with it.
 *
 * Read-only, deliberately and permanently: `reviews` is append-only by rule
 * (`allow update, delete: if false`), so there is nothing here to edit, reply to
 * or remove, and no control that would imply otherwise.
 *
 * With no `identity`, a lesson with no review renders nothing at all — no empty
 * state and no prompt, since the player's prompt to leave one already exists on
 * LessonDeliveredDetail and is untouched by this.
 *
 * With an `identity`, the card always renders. That row is the only thing naming
 * the player on the coach's screen, so it cannot be conditional on a review
 * existing; the review is the part that comes and goes. A standalone fallback
 * row was the alternative and would have meant two copies of the same markup,
 * one of which would eventually stop matching the other.
 *
 * It fetches its own review rather than taking one as a prop. LessonDeliveredDetail
 * already loads one for its prompt, so that screen reads the same document twice —
 * a single-field equality query on a collection with one row per lesson. The
 * alternative was threading state through two layouts there and adding a fetch to
 * the two screens that have none, for a duplicate read of a few hundred bytes.
 */

const STAR = 'M12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 Z';

/** #FACC15 is the rating yellow the profile screens already use. */
function Stars({ rating }: { rating: number }) {
  return (
    <div className="flex items-center gap-1" role="img" aria-label={`${rating} out of 5 stars`}>
      {[1, 2, 3, 4, 5].map((n) => (
        <svg
          key={n}
          width="20" height="20" viewBox="0 0 24 24" aria-hidden="true"
          fill={n <= rating ? '#FACC15' : 'rgba(60,60,67,0.18)'}
        >
          <path d={STAR} />
        </svg>
      ))}
    </div>
  );
}

export default function LessonReviewBlock({
  lessonId,
  viewer,
  className = '',
  identity,
}: {
  lessonId: string;
  /** Chooses the header. The review content itself is the same on both sides. */
  viewer: 'player' | 'coach';
  className?: string;
  /**
   * The identity row to lead with, passed as markup rather than as fields.
   * The coach screen's row carries two profile-opening buttons, a status line
   * with its icon, a UTR badge and a parent-account suffix; threading six pieces
   * of screen state in here to rebuild it would have been a chance to lose one.
   * Moving the markup itself cannot lose anything.
   */
  identity?: ReactNode;
}) {
  const [review, setReview] = useState<Review | null>(null);

  useEffect(() => {
    let cancelled = false;
    getReviewByLessonId(lessonId)
      .then((r) => { if (!cancelled) setReview(r); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [lessonId]);

  if (!review && !identity) return null;

  const comment = review?.comment?.trim();
  const left = review?.createdAt
    ? new Date(review.createdAt).toLocaleDateString('en-GB', {
      day: 'numeric', month: 'short', year: 'numeric',
    })
    : null;

  return (
    /* No header outside the card on either side. The coach's card leads with
       the player's identity, so labelling the whole thing "REVIEW" would misname
       it; the player's label moved inside for the same reason the coach's did,
       and leaving it outside as well would have printed the word twice. */
    <DetailSection className={className}>
      {/* No card of its own. DetailSection already draws exactly the surface
          this needs — white, radius 12, padding 16 — so a bounded card here
          would be a second one inside it.
          gap-3 on the column, so a review with no comment closes up rather than
          leaving the space one would have occupied. */}
      <div className="flex flex-col gap-3">
        {identity}

        {/* A hairline between who and what, so the two halves read as related
            rather than as one run-on block. Only when both are present. */}
        {identity && review && (
          <div style={{ height: 1, background: 'rgba(60,60,67,0.12)' }} />
        )}

        {review && (
          <>
            {/* §8.2's size and colour — 13/18 semibold, rgba(60,60,67,0.6) —
                but sentence case and no letter-spacing, deliberately.
                8.2's uppercase header is a page-structure device: DetailSection
                states the rule in its own doc comment, that it "sits ABOVE the
                white section, on the page background, never inside the card".
                Set inside a card it reads as chrome that wandered in. Same
                weight in the hierarchy, subordinate to the Body 17/22 comment
                below it, without importing a page-level device. */}
            <div className="text-[13px] leading-[18px] font-semibold text-[rgba(60,60,67,0.6)]">
              {viewer === 'player' ? 'Your review' : 'Review'}
            </div>

            {/* No numeral beside them. Five filled stars already say five, and
                this is one player's whole-number rating, not an average that
                needs a decimal. Stars renders directly into the column, without
                the flex row the number used to share — a wrapper with one child
                and a gap-2 would leave the space the number vacated. */}
            <Stars rating={review.rating} />

            {comment && (
              <p className="text-[17px] leading-[22px] tracking-[-0.43px] text-[#1C1C1C] whitespace-pre-wrap">
                {comment}
              </p>
            )}

            {left && (
              <p className="text-[13px] leading-[18px] text-[rgba(60,60,67,0.6)]">Left on {left}</p>
            )}
          </>
        )}
      </div>
    </DetailSection>
  );
}
